/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import { connect, useDispatch } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import Logo from "../../assets/logo-mark.png";
import { logoutUser } from "../../redux/users/actions";
import { getLoggedInUser } from "../../utils/storageUtils";
import { logoutHelper } from "../../utils/logoutHelper";
import {
  canAccessRoute,
  getAdditionalRoles,
  getPrincipalRole,
  isDoctor,
  isStaff,
  routePaths,
} from "../../constants/roles";
import RoleBadge from "../Others/RoleBadge";
import Spinner from "../Others/Spinner";
import AvatarMui from "../legacy/Avatar";

type Props = {
  profile: any;
  children: React.ReactElement;
  showSpinner?: boolean;
};

const DashboardWrapper = ({
  profile,
  children,
  showSpinner = false,
}: Props) => {
  const dispatch = useDispatch();
  const location = useLocation();

  // Utilisateur courant : le `profile` du store quand il est chargé, sinon
  // `userdata` du localStorage. Lu au rendu (jamais au chargement du module)
  // pour rester juste après un changement de session, et résolu une seule
  // fois ici — le menu comme l'en-tête s'appuient dessus.
  const currentUser = profile?.role ? profile : getLoggedInUser();

  // Match exact + sous-routes (ex. /patient/42 highlight /patient).
  // L'item /profile reste actif uniquement sur /profile pour ne pas
  // entrer en collision avec d'autres routes qui pourraient commencer
  // par /profile (aucune aujourd'hui, mais sécurité défensive).
  const isMenuActive = (link: string) => {
    if (!link || link === "#") return false;
    const path = location.pathname;
    return path === link || path.startsWith(link + "/");
  };

  // Migration jQuery → React (2026-05-01) : l'ancien toggle dans
  // public/script.js manipulait les classes via $(...).toggleClass. Lors du
  // remontage React (route change), l'état était perdu et le bouton vert
  // ne faisait plus rien. Désormais l'état vit dans React et les classes
  // sidebar/section sont calculées au render.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const toggleSidebar = () => setSidebarCollapsed((c) => !c);

  const handleLogout = () => {
    dispatch(logoutUser());
  };

  const DEFAULT_DELAY_TIME = 86000000; // 2 days

  const onTimeout = () => {
    // Idle 2j → purge complète de la session (access + refresh + expiry +
    // userdata) puis redirection — mêmes clés que logout manuel pour éviter
    // un refresh préemptif qui ressusciterait la session.
    logoutHelper();
  };

  useIdleTimer({
    onIdle: onTimeout,
    timeout: DEFAULT_DELAY_TIME,
    throttle: 500,
    startOnMount: true,
    name: "idle-timer",
    crossTab: true,
    events: [],
  });

  type IMenu = {
    link: string;
    name: string;
    icon: string;
  };

  // Autorité « médecin » = rôle principal OU rôle additionnel : un
  // administrateur qui exerce comme médecin doit voir sa propre file.
  const isDoc = isDoctor(currentUser);

  // Entrée file d'attente (DEC-8) : le médecin voit SA file, la
  // secrétaire/l'administrateur voient la liste complète. Les autres rôles
  // n'ont pas de file d'attente, donc pas d'entrée de menu.
  const queueMenuItem: IMenu[] = isStaff(currentUser)
    ? [
        {
          link: isDoc ? routePaths.QUEUE_DOCTOR : routePaths.QUEUE_SECRETARY,
          name: "Liste d'attente",
          icon: "bx-list-ul",
        },
      ]
    : [];

  // Convention iconographique (Design system §Iconographie) : un glyphe
  // sémantiquement distinct par item — pas de réutilisation. Boxicons.
  const menu: IMenu[] = [
    { link: routePaths.PATIENTS, name: "Patients", icon: "bx-user" },
    ...queueMenuItem,
    { link: routePaths.APPOINTMENTS, name: "Rendez-vous", icon: "bx-calendar-event" },
    { link: routePaths.DOCTORS, name: "Médecins", icon: "bx-plus-medical" },
    { link: routePaths.INSURERS, name: "Assureur/Entreprise", icon: "bx-shield-alt-2" },
    { link: routePaths.ACTS, name: "Acts", icon: "bx-clipboard" },
    { link: routePaths.PHARMACY, name: "Pharmacie", icon: "bx-capsule" },
    { link: routePaths.USERS, name: "Utilisateurs", icon: "bx-group" },
    { link: routePaths.REPORTS, name: "Bilans", icon: "bx-bar-chart-alt-2" },
    { link: routePaths.PROFILE, name: "Profile", icon: "bx-user-circle" },
  ];

  // Le menu et les gardes de route dérivent de la même liste blanche
  // (`STAFF_ROUTES_BY_ROLE`) : aucun item affiché ne peut mener à une
  // redirection pour cause de droits insuffisants. `canAccessRoute` fait
  // l'union des périmètres de toutes les autorités effectives, donc le
  // menu d'un profil multi-rôles est la réunion de ses menus.
  const visibleMenu = menu.filter((item) => canAccessRoute(currentUser, item.link));

  return (
    <>
      {showSpinner && <Spinner />}
      <div className="tw-scroll-up-btn h-10 w-10 fixed right-6 bottom-0 z-40 flex items-center justify-center rounded-md bg-green-500 hover:bg-green-600 text-white cursor-pointer opacity-0 pointer-events-none transition-all ease-in-out duration-300">
        <i className="bx bxs-chevrons-up text-xl"></i>
      </div>
      {/*
       * Sidebar : la classe `lc-close` est désormais pilotée par React.
       * Largeur : par défaut, mobile = 78px (replié) / desktop = 250px ;
       * quand `lc-close` est appliqué, la convention est inversée
       * (mobile déplié, desktop replié) — comportement identique à
       * l'ancien jQuery, à un setState près.
       */}
      <div
        className={
          "lc-sidebar group/child peer fixed h-full px-[10px] py-[14px] bg-white border-r border-slate-200 z-[100] transition-[width] duration-300 ease-linear " +
          (sidebarCollapsed
            ? "lc-close w-[250px] md:w-[78px]"
            : "w-[78px] md:w-[250px]")
        }
      >
        <header className="relative">
          <div className="flex items-center">
            <span className="min-w-[60px] flex items-center justify-center rounded-md">
              <img
                src={Logo}
                alt="logo la compassion"
                className="h-9 w-9 object-contain"
              />
            </span>
            <span className="lc-logo-text opacity-0 md:opacity-100 group-[.lc-close]/child:opacity-100 md:group-[.lc-close]/child:opacity-0 text-slate-700 text-lg font-semibold whitespace-nowrap transition-all duration-300 ease-in-out">
              La Compassion
            </span>
          </div>
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={sidebarCollapsed ? "Déplier la sidebar" : "Replier la sidebar"}
            className="absolute top-1/2 -right-3 flex h-7 w-7 items-center justify-center bg-green-600 hover:bg-green-700 text-white cursor-pointer -translate-y-1/2 rounded-full shadow-md shadow-green-600/30 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-emerald-500/30"
          >
            <i
              className={
                "bx bx-chevron-left text-lg transition-transform duration-300 " +
                (sidebarCollapsed
                  ? "rotate-0 md:rotate-180"
                  : "rotate-180 md:rotate-0")
              }
            />
          </button>
        </header>
        <div className="h-[calc(100%-55px)] flex flex-col justify-between overflow-x-hidden overflow-y-scroll">
          <div className="mt-10">
            <ul>
              {visibleMenu.map((item, idx) => {
                const active = isMenuActive(item.link);
                return (
                  <li key={idx} className="relative flex items-center h-12">
                    {/* Accent bar emerald à gauche quand actif — fixé sur
                        l'<li> pour que la barre soit alignée même si le
                        Link a des paddings/marges variables. */}
                    {active && (
                      <span
                        aria-hidden
                        className="absolute left-0 top-1/2 -translate-y-1/2 h-7 w-[3px] rounded-r bg-emerald-500"
                      />
                    )}
                    <Link
                      to={item.link}
                      aria-current={active ? "page" : undefined}
                      className={
                        "group flex items-center h-full w-full rounded-md transition-all ease-in-out duration-300 " +
                        (active
                          ? "bg-emerald-50"
                          : "bg-transparent hover:bg-green-100")
                      }
                    >
                      <i
                        className={
                          "bx " +
                          item.icon +
                          " flex items-center justify-center h-full min-w-[60px] text-xl rounded-md transition-all ease-in-out duration-300 " +
                          (active
                            ? "text-emerald-600"
                            : "text-slate-500 group-hover:text-green-600")
                        }
                      />
                      <span
                        className={
                          "text-[13px] font-semibold whitespace-nowrap transition-all ease-in-out duration-300 " +
                          (active
                            ? "text-emerald-700"
                            : "text-slate-500 group-hover:text-green-500")
                        }
                      >
                        {item.name}
                      </span>
                    </Link>
                  </li>
                );
              })}
              {/* <li className="flex items-center h-12">
                <Link
                  to="#"
                  className="group flex items-center h-full w-full bg-transparent hover:bg-green-100 rounded-md transition-all ease-in-out duration-300"
                >
                  <i className="bx bx-bell flex items-center justify-center h-full min-w-[60px] text-xl text-slate-500 group-hover:text-green-500 rounded-md transition-all ease-in-out duration-300"></i>
                  <span className="text-[13px] text-slate-500 group-hover:text-green-500 font-semibold whitespace-nowrap transition-all ease-in-out duration-300">
                    Notifications
                  </span>
                </Link>
              </li>
              <li className="flex items-center h-12">
                <Link
                  to="#"
                  className="group flex items-center h-full w-full bg-transparent hover:bg-green-100 rounded-md transition-all ease-in-out duration-300"
                >
                  <i className="bx bx-cog flex items-center justify-center h-full min-w-[60px] text-xl text-slate-500 group-hover:text-green-500 rounded-md transition-all ease-in-out duration-300"></i>
                  <span className="text-[13px] text-slate-500 group-hover:text-green-500 font-semibold whitespace-nowrap transition-all ease-in-out duration-300">
                    Paramètres
                  </span>
                </Link>
              </li> */}
            </ul>
          </div>
          <ul className="space-y-[10px]">
            <li className="flex items-center h-12" onClick={handleLogout}>
              <Link
                to="#"
                className="group flex items-center h-full w-full bg-transparent hover:bg-green-100 rounded-md transition-all ease-in-out duration-300"
              >
                <i className="bx bx-log-out flex items-center justify-center h-full min-w-[60px] text-xl text-slate-500 group-hover:text-green-500 rounded-md transition-all ease-in-out duration-300"></i>
                <span className="text-[13px] text-slate-500 group-hover:text-green-500 font-semibold whitespace-nowrap transition-all ease-in-out duration-300">
                  Déconnexion
                </span>
              </Link>
            </li>
            <li className="relative flex items-center h-12 bg-green-100 rounded-md transition-all ease-in-out duration-300">
              <div className="h-[50px] w-[60px]">
                <i className="bx bx-moon absolute flex items-center justify-center h-full min-w-[60px] text-xl text-slate-500 rounded-md lc-moon"></i>
                <i className="bx bx-sun absolute flex items-center justify-center h-full min-w-[60px] text-xl text-slate-500 rounded-md opacity-0 lc-sun"></i>
              </div>
              <span className="lc-mode-text opacity-0 md:opacity-100 group-[.lc-close]/child:opacity-100 md:group-[.lc-close]/child:opacity-0 text-[13px] text-slate-500 group-hover:text-white font-semibold whitespace-nowrap transition-all ease-in-out duration-300">
                Dark Mode
              </span>
              <div className="absolute right-0 h-full min-w-[60px] flex items-center justify-center cursor-pointer rounded-md transition-all ease-in-out duration-300before:">
                <span className="lc-switch relative h-[22px] w-10 bg-gray-300 rounded-[25px] before:absolute before:top-1/2 before:left-[5px] before:h-[15px] before:w-[15px] before:bg-white before:rounded-full before:-translate-y-1/2 before:transition-all before:ease-in-out before:duration-300"></span>
              </div>
            </li>
          </ul>
        </div>
      </div>
      {/*
       * Section principale : aligne sa largeur sur l'état React de la
       * sidebar via le sélecteur peer-* (peer = .lc-sidebar grâce au
       * `peer` posé plus haut). Idem pour le nav (group-*).
       */}
      <section
        className={
          "lc-section relative group top-0 min-h-screen bg-white transition-all ease-in-out duration-300 " +
          (sidebarCollapsed
            ? "lc-close-group left-[250px] md:left-[78px] w-[calc(100%-250px)] md:w-[calc(100%-78px)]"
            : "left-[78px] md:left-[250px] w-[calc(100%-78px)] md:w-[calc(100%-250px)]")
        }
      >
        <nav
          className={
            "lc-admin-head fixed flex items-center justify-end h-20 bg-white z-[90] px-4 md:px-10 shadow-sm border-b border-slate-100 transition-all ease-in-out duration-300 " +
            (sidebarCollapsed
              ? "left-[250px] md:left-[78px] w-[calc(100%-250px)] md:w-[calc(100%-78px)]"
              : "left-[78px] md:left-[250px] w-[calc(100%-78px)] md:w-[calc(100%-250px)]")
          }
        >
          {(() => {
            const fullName =
              (currentUser?.firstName || "") +
              " " +
              (currentUser?.lastName || "");
            const displayName = (isDoc ? "Dr " : "") + fullName.trim();
            // Identité affichée = rôle principal ; les rôles cumulés sont
            // rendus en pastilles discrètes à côté.
            const principalRole = getPrincipalRole(currentUser);
            const additionalRoles = getAdditionalRoles(currentUser);
            return (
              <div className="flex items-center gap-3 md:gap-4">
                <div className="hidden sm:flex flex-col items-end leading-tight gap-1">
                  <p className="text-sm font-semibold text-slate-800">
                    {displayName || "—"}
                  </p>
                  {principalRole && (
                    <div className="flex items-center justify-end gap-1 flex-wrap">
                      <RoleBadge role={principalRole} />
                      {additionalRoles.map((role: string) => (
                        <RoleBadge
                          key={role}
                          role={role}
                          variant="additional"
                        />
                      ))}
                    </div>
                  )}
                </div>
                <AvatarMui link={""} text={displayName} />
              </div>
            );
          })()}
        </nav>
        <div className="px-4 md:px-10 py-28">{children}</div>
      </section>
    </>
  );
};

const mapStateToProps = ({ usersReducer }: any) => ({
  profile: usersReducer.profile,
});

export default connect(mapStateToProps)(DashboardWrapper);
